import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faInfoCircle,
  faExclamationCircle,
  faTimes,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { useSound } from '../../context/SoundContext'; 
import styles from './Toast.module.css';

type ToastType = 'success' | 'error' | 'info' | 'code';

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ 
  message, 
  type = 'success', 
  onClose, 
  duration = 3000 
}: ToastProps) {
  const { playSound } = useSound();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const soundType = type === 'code' ? 'info' : type;
    playSound(soundType);

    timerRef.current = setTimeout(onClose, duration);
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration, onClose, type, playSound]);

  const handleManualClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onClose();
  };

  const icon =
    type === 'success'
      ? faCheck
      : type === 'error'
      ? faExclamationCircle
      : type === 'code'
      ? faKey
      : faInfoCircle;

  return (
    <div className={`${styles.toast} ${styles[type]}`}>
      <div className={styles.iconWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.icon} />
      </div>

      <span className={styles.message}>{message}</span>

      <button 
        className={styles.closeButton} 
        onClick={handleManualClose}
        aria-label="Close notification"
      >
        <FontAwesomeIcon icon={faTimes} className={styles.closeIcon} />
      </button>
    </div>
  );
}